import { useEffect, useSyncExternalStore } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import type { KeycloakClientRole } from '../auth/clientRoles';
import { hasAnyClientRole } from '../auth/roles';
import { useI18n } from '../i18n/useI18n';
import {
  getTenantAccessBlocked,
  registerTenantAccessNavigate,
  subscribeTenantAccessBlocked,
  TENANT_DEACTIVATED_PATH,
} from '../query/tenantAccessBlocked';
import './AuthGate.css';

function AuthLoading() {
  const { translate } = useI18n();
  return (
    <div className="liks-auth-loading" role="status" aria-live="polite">
      <span className="liks-auth-loading__label">
        {translate('common.loading')}
      </span>
    </div>
  );
}

/** Registers React Router navigate for tenant-deactivated redirects outside components. */
export function TenantAccessNavigationBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    registerTenantAccessNavigate(navigate);
    return () => {
      registerTenantAccessNavigate(null);
    };
  }, [navigate]);

  return null;
}

/** Redirects into the deactivated page when tenant access has been blocked. */
export function RequireActiveTenant() {
  const blocked = useSyncExternalStore(
    subscribeTenantAccessBlocked,
    getTenantAccessBlocked,
    getTenantAccessBlocked,
  );

  if (blocked) {
    return <Navigate to={TENANT_DEACTIVATED_PATH} replace />;
  }

  return <Outlet />;
}

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, login } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isAuthenticated, isLoading, login]);

  if (isLoading || !isAuthenticated) {
    return <AuthLoading />;
  }

  return <Outlet />;
}

type RequireClientRoleProps =
  | { role: KeycloakClientRole; roles?: never }
  | { roles: readonly KeycloakClientRole[]; role?: never };

export function RequireClientRole(props: RequireClientRoleProps) {
  const { isAuthenticated, isLoading, user, login } = useAuth();
  const allowedRoles = props.roles ?? [props.role];

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isAuthenticated, isLoading, login]);

  if (isLoading || !isAuthenticated) {
    return <AuthLoading />;
  }

  if (!hasAnyClientRole(user, allowedRoles)) {
    return <Navigate to="/knowledge" replace />;
  }

  return <Outlet />;
}

export function RootRedirect() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const blocked = useSyncExternalStore(
    subscribeTenantAccessBlocked,
    getTenantAccessBlocked,
    getTenantAccessBlocked,
  );

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isAuthenticated, isLoading, login]);

  if (isLoading || !isAuthenticated) {
    return <AuthLoading />;
  }

  if (blocked) {
    return <Navigate to={TENANT_DEACTIVATED_PATH} replace />;
  }

  return <Navigate to="/knowledge" replace />;
}
